import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Bot,
  CheckCircle2,
  ExternalLink,
  Loader2,
  RotateCcw,
  Send,
  Sparkles,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react'
import {
  discardIntake,
  getAgentHealth,
  getIntake,
  sendIntakeMessage,
  startIntake,
  submitIntake,
  type IntakeView,
} from '../../lib/api'
import {
  describeError,
  describeMissing,
  draftChips,
  formatTime,
  readStoredRequestId,
  statusPresentation,
  visibleMessages,
  writeStoredRequestId,
} from './presenter'

interface AssistantPanelProps {
  onClose: () => void
}

/**
 * Side panel for the request-intake agent.
 *
 * Talks to `/api/agent/*`, which proxies to the chat-agent Worker. The
 * conversation is addressed by an `ir_…` request id held in sessionStorage, so
 * closing the panel or navigating between routes keeps the thread; a new tab
 * starts fresh.
 *
 * Two things worth knowing about the rendering:
 *  - `tool` messages are filtered out (see presenter.visibleMessages). Tool
 *    results are written for the model, not the reporter.
 *  - Filing is offered as an explicit button, not left to the model. The agent
 *    exposes `POST /intake/:id/submit` precisely so the UI never depends on the
 *    model choosing to call its own tool.
 */
export function AssistantPanel({ onClose }: AssistantPanelProps) {
  const qc = useQueryClient()
  const [requestId, setRequestId] = useState<string | null>(() => readStoredRequestId())
  const [input, setInput] = useState('')
  const [error, setError] = useState<{ message: string; recoverable: boolean } | null>(null)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // ─── Availability ──────────────────────────────────────────────
  // The proxy answers 503 when the CHAT_AGENT service binding is absent (local
  // dev, or an environment where the agent Worker isn't deployed). Checking up
  // front turns that into an explanation instead of a failed first message.
  const health = useQuery({
    queryKey: ['agent-health'],
    queryFn: getAgentHealth,
    retry: false,
    staleTime: 5 * 60 * 1000,
  })

  // ─── Conversation ──────────────────────────────────────────────
  const intake = useQuery({
    queryKey: ['intake', requestId],
    queryFn: () => getIntake(requestId!),
    enabled: !!requestId,
    retry: false,
    // The conversation only changes through this panel, and every mutation
    // writes the returned view straight into the cache. Refetching would just
    // be a second round trip for data we already have.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })

  // A stored id the agent rejects (hand-edited, or from an older id scheme)
  // should quietly start a new conversation rather than wedge the panel.
  // Deliberately narrow: a 503 means the agent is down, not that the
  // conversation is gone, and dropping the thread over a blip would lose work.
  useEffect(() => {
    const status = (intake.error as { status?: number } | null)?.status
    if (requestId && (status === 400 || status === 404)) {
      writeStoredRequestId(null)
      setRequestId(null)
    }
  }, [intake.error, requestId])

  const view: IntakeView | null = intake.data ?? null
  const status = statusPresentation(view)
  const messages = useMemo(() => visibleMessages(view?.messages), [view?.messages])

  /** Adopts the view a mutation returned, without a second round trip. */
  const adopt = (next: IntakeView) => {
    setRequestId(next.requestId)
    writeStoredRequestId(next.requestId)
    qc.setQueryData(['intake', next.requestId], next)
    setError(null)
  }

  const fail = (err: unknown) => setError(describeError(err))

  const send = useMutation({
    mutationFn: (message: string) =>
      requestId
        ? sendIntakeMessage(requestId, message)
        : startIntake({ message, sourceUrl: window.location.href }),
    onSuccess: adopt,
    onError: fail,
  })

  const submit = useMutation({
    mutationFn: () => submitIntake(requestId!),
    onSuccess: adopt,
    onError: fail,
  })

  const discard = useMutation({
    mutationFn: () => discardIntake(requestId!),
    onSuccess: adopt,
    onError: fail,
  })

  const busy = send.isPending || submit.isPending || discard.isPending

  // ─── Panel behaviour ───────────────────────────────────────────

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Follow the conversation as it grows, and while the agent is thinking.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages.length, send.isPending])

  const unavailable = health.isError || (health.data && !health.data.ok)
  const canType = status.canSend && !unavailable

  /**
   * `disabled={busy}` alone is not enough. React Query does not dedupe
   * mutations, and a second click can land before React re-renders with the
   * pending state — which files the same request twice. Every mutating action
   * re-checks `busy` at the moment it fires.
   */
  const handleSubmit = () => {
    if (busy || !requestId) return
    submit.mutate()
  }

  const handleDiscard = () => {
    if (busy || !requestId) return
    // `confirm` yields the stack, so re-check rather than trusting the value
    // captured before the dialog opened.
    if (!confirm('Discard this request? The conversation will be closed.')) return
    if (busy) return
    discard.mutate()
  }

  const handleSend = () => {
    const message = input.trim()
    if (!message || busy || !canType) return
    setInput('')
    send.mutate(message)
  }

  const startAnother = () => {
    writeStoredRequestId(null)
    setRequestId(null)
    setInput('')
    setError(null)
    inputRef.current?.focus()
  }

  return (
    <div className="assistant-backdrop" onClick={onClose}>
      <div
        className="assistant-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Studio OS assistant"
      >
        {/* ─── Header ─────────────────────────────────────────── */}
        <div className="assistant-panel__header">
          <div className="assistant-panel__title-row">
            <span className="assistant-panel__mark" aria-hidden="true">
              <Sparkles size={15} />
            </span>
            <div className="assistant-panel__titles">
              <h2 className="assistant-panel__title">Assistant</h2>
              <span className="assistant-panel__subtitle">
                Report a bug or request a feature
              </span>
            </div>
          </div>
          <div className="assistant-panel__header-actions">
            {view && (
              <span className={`assistant-status assistant-status--${status.tone}`}>
                {status.label}
              </span>
            )}
            <button className="btn-icon" onClick={onClose} aria-label="Close assistant">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* ─── Draft summary ──────────────────────────────────── */}
        {view && <DraftSummary view={view} />}

        {status.note && (
          <p className="assistant-panel__note">{status.note}</p>
        )}

        {/* ─── Transcript ─────────────────────────────────────── */}
        <div className="assistant-panel__body">
          {unavailable ? (
            <UnavailableNotice />
          ) : intake.isLoading && requestId ? (
            <div className="assistant-panel__loading">
              <div className="loading-spinner" />
            </div>
          ) : messages.length === 0 ? (
            <EmptyState onPick={(text) => setInput(text)} disabled={!canType} />
          ) : (
            <div className="assistant-thread">
              {messages.map((m) => (
                <div
                  key={m.id}
                  className={`assistant-msg assistant-msg--${m.role}`}
                >
                  {m.role === 'assistant' && (
                    <span className="assistant-msg__avatar" aria-hidden="true">
                      <Bot size={13} />
                    </span>
                  )}
                  <div className="assistant-msg__bubble">
                    <p className="assistant-msg__text">{m.content}</p>
                    <span className="assistant-msg__time">{formatTime(m.at)}</span>
                  </div>
                </div>
              ))}

              {send.isPending && (
                <div className="assistant-msg assistant-msg--assistant">
                  <span className="assistant-msg__avatar" aria-hidden="true">
                    <Bot size={13} />
                  </span>
                  <div className="assistant-msg__bubble assistant-msg__bubble--thinking">
                    <span className="assistant-typing" aria-label="Assistant is typing">
                      <i /><i /><i />
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* ─── Filed banner ───────────────────────────────────── */}
        {view?.issue && (
          <div className="assistant-filed">
            <CheckCircle2 size={15} className="text-success" />
            <span>
              Filed as <strong>{view.issue.key}</strong>
            </span>
            {view.issue.url && (
              <a
                href={view.issue.url}
                target="_blank"
                rel="noopener noreferrer"
                className="assistant-filed__link"
              >
                Open <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}

        {/* ─── Error ──────────────────────────────────────────── */}
        {error && (
          <div className="assistant-error" role="alert">
            <TriangleAlert size={14} />
            <span>{error.message}</span>
            {error.recoverable && (
              <button className="assistant-error__dismiss" onClick={() => setError(null)}>
                Dismiss
              </button>
            )}
          </div>
        )}

        {/* ─── Composer / closing actions ─────────────────────── */}
        <div className="assistant-panel__footer">
          {status.isClosed ? (
            <button className="btn btn-ghost btn-sm btn-full" onClick={startAnother}>
              <RotateCcw size={13} /> Start another request
            </button>
          ) : (
            <>
              {status.canSubmit && requestId && (
                <div className="assistant-actions">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleSubmit}
                    disabled={busy}
                  >
                    {submit.isPending ? <Loader2 size={13} className="spin" /> : <CheckCircle2 size={13} />}
                    {submit.isPending ? 'Filing…' : 'File it'}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={handleDiscard}
                    disabled={busy}
                  >
                    <Trash2 size={13} /> Discard
                  </button>
                </div>
              )}

              <form
                className="assistant-composer"
                onSubmit={(e) => {
                  e.preventDefault()
                  handleSend()
                }}
              >
                <textarea
                  ref={inputRef}
                  className="assistant-composer__input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={
                    unavailable
                      ? 'The assistant is unavailable'
                      : requestId
                        ? 'Reply…'
                        : "What's broken, or what would you like added?"
                  }
                  rows={2}
                  disabled={!canType || busy}
                  onKeyDown={(e) => {
                    // Enter sends, Shift+Enter breaks the line — chat convention.
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSend()
                    }
                  }}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm assistant-composer__send"
                  disabled={!input.trim() || busy || !canType}
                  aria-label="Send message"
                >
                  {send.isPending ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * DRAFT SUMMARY — what the agent has understood so far
 * ═══════════════════════════════════════════════════════════════════ */

function DraftSummary({ view }: { view: IntakeView }) {
  const chips = draftChips(view.draft)
  const missing = describeMissing(view.missing)
  const hasTitle = !!view.draft.title

  // Nothing recorded and nothing outstanding — don't render an empty strip.
  if (!hasTitle && chips.length === 0 && !missing) return null

  return (
    <div className="assistant-draft">
      {hasTitle && <p className="assistant-draft__title">{view.draft.title}</p>}

      {chips.length > 0 && (
        <div className="assistant-draft__chips">
          {chips.map((c) => (
            <span key={c.key} className={`chip chip-sm assistant-chip assistant-chip--${c.tone}`}>
              {c.label}
            </span>
          ))}
        </div>
      )}

      {missing ? (
        <p className="assistant-draft__missing">Still needs {missing}.</p>
      ) : view.submittable ? (
        <p className="assistant-draft__ready">
          <CheckCircle2 size={12} /> Everything needed — file it when you&rsquo;re happy.
        </p>
      ) : null}
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════
 * EMPTY + UNAVAILABLE STATES
 * ═══════════════════════════════════════════════════════════════════ */

const PROMPTS = [
  'Something on this page is broken',
  "I'd like a new feature",
  'How do I do something?',
] as const

function EmptyState({ onPick, disabled }: { onPick: (text: string) => void; disabled: boolean }) {
  return (
    <div className="assistant-empty">
      <span className="assistant-empty__mark" aria-hidden="true">
        <Sparkles size={22} />
      </span>
      <p className="assistant-empty__lead">
        Tell me what you need and I&rsquo;ll turn it into a ticket for the team.
      </p>
      <p className="assistant-empty__hint">
        I&rsquo;ll ask a couple of follow-ups, then show you the summary before anything is filed.
      </p>
      <div className="assistant-empty__prompts">
        {PROMPTS.map((p) => (
          <button
            key={p}
            className="assistant-prompt"
            onClick={() => onPick(p)}
            disabled={disabled}
            type="button"
          >
            {p}
          </button>
        ))}
      </div>
    </div>
  )
}

function UnavailableNotice() {
  return (
    <div className="assistant-empty">
      <span className="assistant-empty__mark assistant-empty__mark--muted" aria-hidden="true">
        <Bot size={22} />
      </span>
      <p className="assistant-empty__lead">The assistant isn&rsquo;t available here.</p>
      <p className="assistant-empty__hint">
        The chat-agent Worker isn&rsquo;t reachable from this environment. It runs in production —
        locally it needs to be started separately.
      </p>
    </div>
  )
}
